import { frameText, type SseFrame } from "@/shared/builder-session";
import { asWorkflowPlan } from "@/features/chat/builder/sse/chat-helpers";

/** Shared SSE → transcript copy so foreground and background stay in sync. */
export const WORKFLOW_COMPLETE_ASSISTANT_TEXT =
  "Workflow finalized and saved as a draft. Deploy from the toolbar when you are ready to publish.";

export function agentStoppedStatusText(data: unknown): string {
  return frameText(data) || "Agent stopped";
}

export function buildStalledStatusText(data: unknown): string {
  const reason = (data as { reason?: string } | null)?.reason;
  return reason?.trim() || "Build paused — no further progress this round.";
}

export function streamErrorStatusText(data: unknown): string {
  return frameText(data) || "Something went wrong while building.";
}

/**
 * Slim tool payloads for the live transcript.
 *
 * args / result stay so Activity rows can expand to the raw JSON.
 * compactMessagesForStorage drops them again before anything reaches disk.
 */
export function slimToolDetails(
  data: unknown,
  pending: boolean,
): Record<string, unknown> | undefined {
  if (!data || typeof data !== "object") return undefined;
  const record = data as Record<string, unknown>;
  const summary = typeof record.summary === "string" ? record.summary.slice(0, 240) : undefined;
  return {
    tool: record.tool,
    phase: pending ? "call" : "result",
    ...(record.args !== undefined ? { args: record.args } : {}),
    ...(record.result !== undefined ? { result: record.result } : {}),
    ...(summary ? { summary } : {}),
  };
}

/**
 * Shared transcript writes for events that look the same in live UI and
 * background cache. Builder-only extras (graph, HITL queues, busy) stay in
 * handleSseFrame.
 *
 * Plans keep full steps — disk size is handled by compactMessagesForStorage.
 */
export type TranscriptFrameOps = {
  append: (type: string, text: string, details?: unknown) => void;
  appendTextDelta: (text: string) => void;
  upsertPlan: (details: unknown) => void;
};

export type TranscriptFrameResult =
  { handled: false } | { handled: true; endStream?: boolean; parkHitl?: boolean };

export function applyTranscriptFrame(
  frame: SseFrame,
  ops: TranscriptFrameOps,
): TranscriptFrameResult {
  const { event, data } = frame;

  if (event === "text-delta") {
    const text = frameText(data);
    if (text) ops.appendTextDelta(text);
    return { handled: true };
  }

  if (event === "tool-call") {
    const tool = String((data as { tool?: string } | null)?.tool ?? "tool");
    ops.append("tool", `Calling ${tool}`, slimToolDetails(data, true));
    return { handled: true };
  }

  if (event === "tool-result") {
    const tool = String((data as { tool?: string } | null)?.tool ?? "tool");
    ops.append("tool", `${tool} completed`, slimToolDetails(data, false));
    return { handled: true };
  }

  if (event === "plan") {
    const plan = asWorkflowPlan(data) ?? data;
    ops.upsertPlan(plan);
    return { handled: true };
  }

  if (event === "complete") {
    ops.append("assistant", WORKFLOW_COMPLETE_ASSISTANT_TEXT);
    return { handled: true, endStream: true };
  }

  if (event === "error") {
    ops.append("status", streamErrorStatusText(data), data);
    return { handled: true, endStream: true };
  }

  if (event === "agent-stopped") {
    ops.append("status", agentStoppedStatusText(data), data);
    return { handled: true, endStream: true };
  }

  if (event === "build-stalled") {
    ops.append("status", buildStalledStatusText(data), data);
    return { handled: true, endStream: true };
  }

  if (event === "stream-ended" || event === "stream-end") {
    return { handled: true, endStream: true };
  }

  if (
    event === "user-question" ||
    event === "composio-exec-approval" ||
    event === "input-required" ||
    event === "build-paused"
  ) {
    return { handled: true, endStream: true, parkHitl: true };
  }

  if (
    event === "composio-exec-resolved" ||
    event === "user-question-resolved" ||
    event === "input-required-resolved" ||
    event === "runtime-input-resolved"
  ) {
    return { handled: true };
  }

  return { handled: false };
}
